import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { addAndUpdateVersion } from "../../../../../services/ProgramService";
import { invalidateVersionListQueries } from "../../../../../query/hooks/QueryHooksLinkedProgrammes";
import { useUnsavedChangesGuard } from "../../../../../hooks/useUnsavedChangesGuard";
import { usePermissionGuard } from "../../../../../hooks/usePermissionGuard";
import useVersionDetailsOptions from "./useVersionDetailsOptions";

/**
 * Drives the Version Details form (SPEC2): field state/validation via formik,
 * the Qualification Co-ordinator / SOR Template / Override Brand option
 * lists, save/cancel, unsaved-changes route/tab-close protection, and the
 * locked/view-only state the panels disable their fields against.
 * @param {object} options.versionData The version row (`getVersion`'s response), or `{}` on create.
 * @param {string | number} options.programId PMC programme id.
 * @param {string | number} [options.versionId] Version id from the route, absent for "create".
 */
function useVersionDetailsForm({
    versionData = {},
    type,
    programId,
    versionId,
    setLoading,
    setRefresh,
}) {
    const history = useHistory();

    const { coordinatorOptions: activeCoordinatorOptions, isCoordinatorLoading, sorTemplateOptions, brandOptions } = useVersionDetailsOptions();

    // Options only list active staff, so a saved co-ordinator who's since been deactivated is added back to keep their name showing.
    const savedCoordinatorId = versionData?.coordinator_id;
    const isSavedCoordinatorMissing = Boolean(savedCoordinatorId && versionData?.coordinator?.name)
        && !activeCoordinatorOptions.some((option) => String(option.value) === String(savedCoordinatorId));
    const coordinatorOptions = isSavedCoordinatorMissing
        ? [
            ...activeCoordinatorOptions,
            { value: savedCoordinatorId, label: versionData.coordinator.name, avatar: versionData.coordinator.avatar },
        ]
        : activeCoordinatorOptions;

    const formik = useFormik({
        enableReinitialize: type !== "create",
        initialValues: type === "create" ? {
            name: "",
            notes: "",
            lock_version_settings: 0,
            coordinator_id: "",
            st_template: "",
            show_assessments: 0,
            show_final_results: 0,
            show_empty_grades: 0,
            override_sor_brand: 0,
            override_brand: "",
            allow_student_portal_gradebook_view: 0,
            allow_student_portal_sor_to_download: 0,
        } : {
            name: type === "clone" ? (versionData?.name ? `${versionData.name} (Copy)` : "") : (versionData?.name || ""),
            notes: versionData?.notes || "",
            lock_version_settings: versionData?.lock_version_settings ?? 0,
            coordinator_id: versionData?.coordinator_id || "",
            st_template: versionData?.st_template ?? "",
            show_assessments: versionData?.show_assessments ?? 0,
            show_final_results: versionData?.show_final_results ?? 0,
            show_empty_grades: versionData?.show_empty_grades ?? 0,
            override_sor_brand: versionData?.override_sor_brand ?? 0,
            override_brand: versionData?.override_brand ?? "",
            allow_student_portal_gradebook_view: versionData?.allow_student_portal_gradebook_view ?? 0,
            allow_student_portal_sor_to_download: versionData?.allow_student_portal_sor_to_download ?? 0,
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
        }),
        onSubmit: async (values) => {
            setLoading(true);

            const payload = {
                ...values,
                pmc_id: programId,
                coordinator_id: values.coordinator_id || null,
                st_template: values.st_template === "" ? null : values.st_template,
                override_brand: values.override_brand === "" ? null : values.override_brand,
            };

            if (type === "details" || type === "clone") {
                payload.id = versionId;

                if (type === "clone") {
                    payload.isDuplicate = true;
                }
            }

            try {
                await addAndUpdateVersion(payload);
                await invalidateVersionListQueries();

                Swal.fire({ icon: "success", title: "Success", text: "Updated Successfully" });

                /*
                 * Matches VersionPageData.js's original branching: an existing versionId (edit,
                 * or clone's source version) refreshes in place unless it's a clone, which
                 * navigates to the new copy's list; a brand-new version always navigates there.
                 */
                if (versionId) {
                    if (type === "clone") {
                        history.push(`/program-management-center/programmes/versions/table/${programId}`);
                    } else {
                        setRefresh(Date.now());
                    }
                } else {
                    history.push(`/program-management-center/programmes/versions/table/${programId}`);
                }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error?.response?.data?.message || "Something went wrong",
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const isEditingExisting = type === "details" && Boolean(versionId);

    /*
     * Scoped to editing an existing version only, matching useProgramDetail.js's own
     * guard scoping - "create"/"clone" navigate away with history.push right after a
     * successful save (still dirty at that point), so guarding them would trip the
     * router block on that same legitimate navigation.
     */
    useUnsavedChangesGuard(formik.dirty, isEditingExisting, history);

    const hasEditAccess = usePermissionGuard({
        scopes: [isEditingExisting ? "pmcversionedit" : "pmcversionadd"],
    }) !== false;

    const handleReset = () => {
        if (type === "create") {
            history.push(`/program-management-center/programmes/versions/table/${programId}`);
        } else {
            formik.resetForm();
        }
    };

    return {
        formik,
        coordinatorOptions,
        isCoordinatorLoading,
        sorTemplateOptions,
        brandOptions,
        handleReset,
        hasEditAccess,
        isVersionLocked: formik.values.lock_version_settings === 1,
    };
}

export default useVersionDetailsForm;
